<script setup name="TvbAds">
import { reactive, nextTick, ref, inject, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import sha256 from 'crypto-js/sha256'
import { generateMixed, isBot, getSubdomain } from './../../utils/index'
import { getAds } from './../../api/app'
import { adsConfig } from './../../constants/index'
import { useCounterStore } from './../../stores/counter'

const emits = defineEmits(['load', 'error'])

// 从全局注入获取窗口宽度
const windowWidth = inject('width')

const props = defineProps({
  /**
   * 广告id 自定义且唯一
   */
  id: {
    type: String,
    default: () => generateMixed(16)
  },
  /**
   * 广告类型 bn(banner)/lrec
   * "superbanner1" for the 1st Super Banner of the page. "superbanner2" for the 2nd Super Banner of the page, etc. Footer Super Banner is hardcoded to be "superbanner99"
   * "lrec1" for the 1st LREC. "lrec2" for the 2nd LREC. etc.
   * "takeover1" for the 1st Outstream Ads
   */
  adType: {
    type: String,
    required: true
  },
  /**
   * 位置
   * 1-節目主頁，2-Featured，3-每集內容，4-節目表總覽，5-節目表Channel
   * 6-主頁(Home Page)，7-Article Main Category Page，8-Article Sub Category Page
   * 9-Article Detail Page，10-Search Result - Article
   * 11-每集內容(video player)，12-Video Main Category Page，13-Video Sub Category Page，14-Video Detail Page
   * 具体见 https://tvbco-my.sharepoint.com/:p:/g/personal/wing_do_tvb_com/ERjfPxk80VJOlfXFbuIrKZIB3eW5iUylAM-paLuhr7wW7g?e=wVk7YL
   */
  position: {
    type: Number,
    required: true
  },
  /** 后缀 */
  unit: {
    type: String,
    default: ''
  },
  /**
   * 广告其他参数
   * 具体见 https://tvbcom.atlassian.net/browse/TC-636
   */
  params: {
    type: Object,
    default: () => {}
  },
  /**
   * 是否显示广告，用于 is_show_ad 控制
   * true - 显示广告（默认）
   * false - 不显示广告
   */
  showAd: {
    type: Boolean,
    default: true
  }
})

const route = useRoute()
const tvbAdsRef = ref(null)
const counter = useCounterStore()

// 懒加载相关状态
const hasInitialized = ref(false) // 广告是否已初始化

const ads = reactive({
  id: `TVBADS${props.id}`,
  loading: true,
  show: false,
  unitPrefix: '',
  list: [],
  type: '',
  refresh: 0,
  size: [],
  adWidth: null,
  adHeight: null,
  interval: null,
  io: null,
  isEmpty: false
})

// OOP 广告类型（splash=插页式），无需 div 容器，GPT 自建容器
// to（outstream）改为普通 defineSlot + 1×1 触发点 + 右下角浮动
const isOutOfPage = props.adType === 'splash'
// 存储当前组件创建的 GPT slot 引用（非响应式），供卸载时 destroySlots 使用
let currentSlot = null

// 同步预分配广告编号，确保按 DOM 顺序递增
// 解决多个 IntersectionObserver 回调顺序不确定导致编号颠倒的问题
const adNumber = (() => {
  switch (props.adType) {
    case 'topbn': {
      const num = counter.topbn
      counter.topbn++
      return num
    }
    case 'sb': {
      const num = counter.sb
      counter.sb++
      return num
    }
    case 'bn': {
      const num = counter.bn
      counter.bn++
      return num
    }
    case 'lrec': {
      const num = counter.lrec
      counter.lrec++
      return num
    }
    case 'wsky': {
      const num = counter.wsky
      counter.wsky++
      return num
    }
    default:
      return null
  }
})()

/** 初始化广告 */
const initAds = (capturedParams) => {
  const config = adsConfig[route.name]
  console.log('[Ads Debug]: adsConfig', config, props.adType, route.name)
  if (config) {
    ads.unitPrefix += config.unit + props.unit + '/' + props.adType
    // splash 和 to 类型无需尺寸判断，只要有匹配的 ad_type 就直接 pushAds
    const skipSizeCheck = ['splash', 'to'].includes(props.adType)
    for (let index = 0; index < ads.list.length; index++) {
      const { height, width, size_setting } = ads.list[index]
      const [minHeight, maxHeight] = height
      const [minWidth, maxWidth] = width
      const { innerHeight, innerWidth } = window
      // 尺寸判断：splash/to 跳过，其他类型正常检查
      const sizeMatched =
        skipSizeCheck ||
        (innerWidth >= (minWidth || -Infinity) &&
          innerWidth <= (maxWidth || Infinity) &&
          innerHeight >= (minHeight || -Infinity) &&
          innerHeight <= (maxHeight || Infinity))
      if (sizeMatched) {
        for (let indexs = 0; indexs < size_setting.length; indexs++) {
          const { ad_type, refresh, size } = size_setting[indexs]
          if (ad_type === props.adType) {
            ads.refresh = refresh
            ads.size = size
            switch (props.adType) {
              case 'to':
                ads.type = 'takeover1'
                break
              case 'topbn':
                ads.type = `topbanner${adNumber}`
                break
              case 'sb':
                ads.type = `superbanner${adNumber}`
                break
              case 'bn':
                ads.type = `banner${adNumber}`
                break
              case 'lrec':
                ads.type = `lrec${adNumber}`
                break
              case 'wsky':
                ads.type = `wsky${adNumber}`
                break
              case 'os':
                ads.type = 'os'
                break
              case 'splash':
                ads.type = 'splash'
                break
              default:
                break
            }
            nextTick(() => pushAds(capturedParams))
          }
        }
      }
    }
  }

  ads.isEmpty = !ads.type
}

/** 加载广告（懒加载入口） */
const loadAd = () => {
  if (hasInitialized.value) return
  hasInitialized.value = true

  // 快照广告参数，防止异步请求期间 props.params 被外部重置（如路由切换重置 adParams）
  const capturedParams = JSON.parse(JSON.stringify(props?.params || {}))

  getAds({ position: props.position, adtype: props.adType }).then(
    ({ data: { ad_unit_prefix, ads_list } }) => {
      ads.unitPrefix = ad_unit_prefix
      ads.list = ads_list
      initAds(capturedParams)
    }
  )
}

/** 添加广告 */
const pushAds = (capturedParams, googletag = window.googletag) => {
  console.log('[Ads Debug]: pushAds', ads.unitPrefix, ads.size, ads.id)
  googletag.cmd.push(function () {
    googletag.enableServices()
    // setCentering 已弃用且无 setConfig 替代，广告居中由组件 CSS (justify-content: center) 实现

    // OOP 广告类型到 GPT OutOfPageFormat 枚举的映射（仅 splash）
    const outOfPageFormatMap = {
      splash: googletag.enums.OutOfPageFormat.INTERSTITIAL
    }

    // 根据 adType 选择插槽定义方式：OOP 广告(splash)用 defineOutOfPageSlot，普通广告用 defineSlot
    let slot
    if (isOutOfPage) {
      // 通过枚举映射获取格式，splash → INTERSTITIAL
      const format = outOfPageFormatMap[props.adType]
      slot = googletag.defineOutOfPageSlot(ads.unitPrefix, format)
      // 页面/设备不支持 OOP 格式时返回 null，直接标记为空广告
      if (!slot) {
        console.warn(`[Ads Debug] ${props.adType} OOP 格式不受支持`)
        ads.isEmpty = true
        ads.loading = false
        return
      }
    } else {
      const slotSize = ads.size
      slot = googletag.defineSlot(ads.unitPrefix, slotSize, ads.id)
    }

    slot.addService(googletag.pubads())
    // 优先使用 loadAd 时快照的 params，兜底读取 props.params
    const sourceParams = capturedParams || props?.params || {}
    const params = JSON.parse(JSON.stringify(sourceParams))
    params.adtype = ads.type
    params.hdid = sha256(localStorage.uuid).toString()
    params.subdomain = getSubdomain()
    if (!sourceParams.vtype) params.vtype = 'x'
    // 迁移自 clearTargeting + setTargeting，用 setConfig 统一设置 targeting
    // 先重置服务级和插槽级 targeting（清除上一个广告的残留参数），再设置当前广告参数
    googletag.setConfig({ targeting: null })
    slot.setConfig({ targeting: null })
    if (Object.keys(params || {}).length) {
      // 构建服务级 targeting 对象，一次性设置（替代逐个 setTargeting）
      const serviceTargeting = {}
      for (const key in params) {
        serviceTargeting[key] = `${params[key]}`
      }
      googletag.setConfig({ targeting: serviceTargeting })
    }
    // 设置插槽级 targeting
    slot.setConfig({ targeting: { adtype: params.adtype } })
    // OOP 广告传 slot 对象，普通广告传 div ID
    googletag.display(isOutOfPage ? slot : ads.id)
    // 保存 slot 引用供卸载时 destroySlots
    currentSlot = slot
    // 每次请求广告后更新 correlator，确保同一页面的下一个广告请求使用不同的 correlator
    googletag.pubads().updateCorrelator()
    // 主动刷新拉取广告内容（配合 disableInitialLoad，display() 只注册不拉取）
    googletag.pubads().refresh([slot])
    googletag.pubads().addEventListener('slotRenderEnded', (event) => {
      if (slot === event.slot) {
        event.params = params
        console.group(
          `Ad「${ads.type}」unit「${
            ads.unitPrefix
          }」slot「${slot.getSlotElementId()}」finished rendering.`
        )
        console.log('所呈现广告的广告客户 ID (Advertiser ID):', event.advertiserId)
        console.log('所呈现广告的广告系列 ID (Campaign ID):', event.campaignId)
        console.log('对所呈现的补余广告出价的公司的 ID (Company IDs):', event.companyIds)
        console.log('已呈现的预订型广告的广告素材 ID (Creative ID):', event.creativeId)
        console.log(
          '所呈现的预订型广告的广告素材模板 ID (Creative Template ID):',
          event.creativeTemplateId
        )
        console.log('广告是否为补余广告 (Is backfill)?:', event.isBackfill)
        console.log('是否针对广告位返回了空广告 (Is empty)?:', event.isEmpty)
        console.log('所呈现广告的标签 ID (Label IDs):', event.labelIds)
        console.log('已呈现的预订型广告的订单项 ID (Line Item ID):', event.lineItemId)
        console.log('所呈现广告素材的像素尺寸 (Size):', event.size)
        console.log(
          '广告位内容是否随呈现的广告而发生更改 (Slot content changed)?',
          event.slotContentChanged
        )
        console.log(
          '已呈现的预订型广告或补余广告的广告素材 ID (Source Agnostic Creative ID):',
          event.sourceAgnosticCreativeId
        )
        console.log(
          '已呈现的预订型广告或补余广告的订单项 ID (Source Agnostic Line Item ID):',
          event.sourceAgnosticLineItemId
        )
        console.log('所呈现的补余广告的收益组的 ID (Yield Group IDs):', event.yieldGroupIds)
        console.log('所呈现广告的参数:', event.params)
        console.groupEnd()
        ads.adWidth = event.size?.[0] || null
        ads.adHeight = event.size?.[1] || null
        ads.isEmpty = event.isEmpty
        if (!event.isEmpty) emits('load', event)
        else emits('error', event)
        ads.loading = false
      }
    })
    // 设置刷新（splash 插页式广告为触发型，无需定时刷新；to 浮动广告保留 IO 刷新）
    if (ads.refresh && props.adType !== 'splash') {
      try {
        ads.io = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            if (ads.interval) {
              clearInterval(ads.interval)
            }
            ads.interval = setInterval(() => {
              googletag.pubads().refresh([slot])
            }, ads.refresh * 1000)
          } else if (ads.interval) {
            clearInterval(ads.interval)
          }
        })
        if (tvbAdsRef.value) {
          ads.io.observe(tvbAdsRef.value)
        }
      } catch (error) {
        console.error(error)
      }
    }
  })
}

// 懒加载：监听广告容器进入可视区域
// to（takeover）和 splash 类型跳过懒加载，立即请求
onMounted(() => {
  if (!isBot() && props.showAd && tvbAdsRef.value) {
    // takeover 和 splash 广告立即加载，不等待进入可视区域
    if (['to', 'splash'].includes(props.adType)) {
      loadAd()
      return
    }
    const lazyLoadIo = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasInitialized.value) {
          // 进入可视区域，触发广告加载
          loadAd()
          lazyLoadIo.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    lazyLoadIo.observe(tvbAdsRef.value)
  }
})

onBeforeUnmount(() => {
  if (ads.interval) {
    clearInterval(ads.interval)
  }
  ads?.io?.disconnect()
  // SPA 路由切换时销毁不再需要的广告位，释放 GPT 资源
  if (currentSlot) {
    const slot = currentSlot
    currentSlot = null
    window.googletag?.cmd?.push(() => {
      window.googletag.destroySlots([slot])
    })
  }
})
</script>

<template>
  <!-- OOP 广告（splash）由 GPT 自建容器，隐藏组件自身 div；to 为普通插槽，组件 div 可见 -->
  <div
    v-show="!ads.isEmpty && !isOutOfPage"
    class="tvb-ads"
    ref="tvbAdsRef"
    :data-type="ads.type || adType"
    :class="{ zero: ads.loading || ads.isEmpty }"
    :style="{
      display: ads.isEmpty || isOutOfPage ? 'none' : null,
      // 页面宽度 970px~1000px 时使用广告实际宽度，或广告宽度 >= 970px 时也使用，其余自适应
      width:
        windowWidth >= 970 && windowWidth <= 1000 && ads.adWidth >= 970 && ads.adWidth
          ? `${ads.adWidth}px`
          : null,
      height: ads.adHeight ? `${ads.adHeight}px` : null,
      // marginTop: ads.loading || ads.isEmpty ? '0px !important' : null,
      // margin: ads.loading || ads.isEmpty ? '0px !important' : null,
      // padding: ads.loading || ads.isEmpty ? '0px !important' : null,
      // height: ads.loading || ads.isEmpty ? '0px !important' : null,
      // minHeight: ads?.size?.[0]?.[1] ? `${ads.size?.[0]?.[1]}px` : null,
      background: ads.isEmpty || ads.loading ? 'rgba(240, 241, 242,0)' : 'rgb(240, 241, 242, 0)'
    }"
  >
    <div
      class="tvb-ads_container"
      :style="{ width: ads.adWidth ? `${ads.adWidth}px` : null }"
      :id="ads.id"
    ></div>
    <div
      v-if="ads.isEmpty || ads.loading"
      class="tvb-ads_container-empty"
      :title="ads.type || adType"
    >
      &nbsp;
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tvb-ads {
  width: 100%;
  height: auto;
  max-width: 100%;
  position: relative;
  background: rgb(240, 241, 242);
  display: flex;
  align-items: center;
  justify-content: center;
  &.zero {
    margin: 0px !important;
    padding: 0px !important;
    height: 0px !important;
  }

  .tvb-ads_container {
    margin: 0 auto;
    width: inherit;
    height: 100%;
    display: flex;
  }

  // Sky Scraper 浮动侧边栏样式
  &[data-type^='wsky'] {
    position: fixed;
    top: var(--header-height, 215px);
    z-index: 100;
    width: auto;
    height: auto;
    max-height: calc(100vh - var(--header-height, 215px));
    overflow: hidden;

    // wsky1 右对齐
    &[data-type='wsky1'] {
      right: 10px;
    }

    // wsky2 左对齐
    &[data-type='wsky2'] {
      left: 10px;
    }
  }

  // Takeover (outstream) 右下角浮动样式
  // 替代原 BOTTOM_ANCHOR 的锚定效果，由自定义 CSS 实现
  &[data-type='takeover1'] {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 100;
    width: auto;
    height: auto;
    // 浮动模式下取消灰底，由广告素材自定背景
    background: transparent;
  }

  // Web Interstitial 全屏居中样式已移除：INTERSTITIAL OOP 广告由 GPT 自建容器管理

  .tvb-ads_container-empty {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 32px;
    font-weight: 500;
    letter-spacing: 8px;
    line-height: 28px;
    text-shadow: 0 0 6px rgba(0, 0, 0, 0.1);
    user-select: none;
  }
}
</style>
